import { useRef, useEffect, useState } from "react";
import { motion, useScroll, useTransform } from "framer-motion";
import { Link } from "react-router-dom";
import { ArrowDown, Plane } from "lucide-react";
import { HERO_IMG } from "../data/site";
import KineticText from "./KineticText";

export default function Hero() {
  const ref = useRef(null);
  const [ready, setReady] = useState(false);

  useEffect(() => {
    const t = setTimeout(() => setReady(true), 50);
    return () => clearTimeout(t);
  }, []);

  const { scrollYProgress } = useScroll({
    target: ref,
    offset: ["start start", "end start"],
  });

  const y = useTransform(scrollYProgress, [0, 1], ["0%", "22%"]);
  const scale = useTransform(scrollYProgress, [0, 1], [1, 1.12]);
  const opacity = useTransform(scrollYProgress, [0, 0.6, 1], [1, 0.7, 0]);

  return (
    <section
      ref={ref}
      data-testid="hero-section"
      className="relative min-h-[100svh] w-full overflow-hidden bg-[color:var(--bs-cream)]"
    >
      {/* Background image with parallax + entry scale */}
      <motion.div
        style={{ y, scale }}
        className="absolute inset-0 -z-0"
      >
        <motion.div
          initial={{ scale: 1.15, opacity: 0 }}
          animate={{ scale: 1, opacity: 1 }}
          transition={{ duration: 1.6, ease: [0.2, 0.8, 0.2, 1] }}
          className="absolute inset-0"
        >
          <img
            src={HERO_IMG}
            alt="Hélicoptère survolant la Belgique"
            className="w-full h-full object-cover"
            loading="eager"
          />
          <div className="absolute inset-0 bg-gradient-to-b from-white/40 via-white/10 to-[color:var(--bs-cream)]/70" />
          <div className="absolute inset-0 bg-gradient-to-r from-white/50 via-transparent to-transparent" />
        </motion.div>
      </motion.div>

      {/* Main hero text */}
      <div className="relative z-10 mx-auto max-w-[1440px] px-6 md:px-10 min-h-[100svh] flex flex-col justify-end pb-28 md:pb-32">
        <div className="grid grid-cols-1 md:grid-cols-12 gap-8 md:gap-6 items-end">
          <motion.p
            data-testid="hero-heading"
            initial={{ opacity: 0, y: 24 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.4, duration: 0.95, ease: [0.2, 0.8, 0.2, 1] }}
            style={{ textShadow: "0 1px 18px rgba(255,255,255,0.9), 0 1px 2px rgba(255,255,255,0.95)" }}
            className="md:col-span-6 lg:col-span-5 text-lg md:text-xl lg:text-2xl text-[color:var(--bs-ink)] font-medium leading-relaxed balance"
          >
            Baptêmes de l'air, initiations au pilotage, transports VIP,
            mariages et vols entreprise — au départ de l'aérodrome de Namur,
            à bord de nos hélicoptères.
          </motion.p>

          <motion.div
            initial={{ opacity: 0, y: 18 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.6, duration: 0.9, ease: [0.2, 0.8, 0.2, 1] }}
            className="md:col-span-4 md:col-start-9 flex flex-col sm:flex-row gap-4"
          >
            <Link
              to="/reservation"
              data-testid="hero-cta-primary"
              className="btn-gold !font-semibold"
            >
              Réserver un vol
              <span aria-hidden>→</span>
            </Link>
            <Link
              to="/flotte"
              data-testid="hero-cta-secondary"
              className="btn-gold !font-semibold"
            >
              <Plane size={14} strokeWidth={2} />
              Nos appareils
            </Link>
          </motion.div>
        </div>
      </div>

      {/* Scroll cue */}
      <motion.div
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        transition={{ delay: 1.6, duration: 0.8 }}
        style={{ opacity }}
        className="absolute bottom-8 md:bottom-10 left-6 md:left-10 z-10 flex items-center gap-3 text-[color:var(--bs-ink)]"
      >
        <motion.div
          animate={{ y: [0, 6, 0] }}
          transition={{ duration: 2.4, repeat: Infinity, ease: "easeInOut" }}
        >
          <ArrowDown size={16} strokeWidth={1.5} />
        </motion.div>
        <span className="overline">Explorer</span>
      </motion.div>

      <div className="absolute bottom-8 md:bottom-10 right-6 md:right-10 z-10 overline text-[color:var(--bs-ink)]">
        Depuis 2026 · BE
      </div>
    </section>
  );
}
